import { ClarionButton } from '@clarion/ClarionButton';
import { Icon } from '@clarion/ClarionIcon';
import ClarionPopover from '@clarion/ClarionPopover';
import { ClarionSwitch as Switch } from '@clarion/ClarionSwitch';
import { ClarionTag as Tag } from '@clarion/ClarionTag';
import Divider from '@clarion/Divider';
import { useSideNavigation } from '../components/navigation/SideNavigationContext';
import { FlagName, getFlagOverrides, getFlags, resetFlagOverrides, setFlagOverride } from './flags';

/**
 * Provides UI to view and override flags from the user's localStorage.
 * The button is visible when flags are overridden, in development mode, or with ?flagOverrides query param is present.
 */
export default function FlagOverrides() {
  const flags = getFlags();
  const flagOverrides = getFlagOverrides();
  const overriddenFlagCount = Object.keys(flagOverrides).length;
  const { isCollapsed } = useSideNavigation();
  const showOverrides =
    overriddenFlagCount > 0 ||
    process.env.NODE_ENV === 'development' ||
    window.location.search.includes('flagOverrides');

  if (!showOverrides) return null;

  const button = (
    <ClarionButton
      variant="minimal"
      size="large"
      icon={<Icon icon="flag" intent={overriddenFlagCount ? 'warning' : 'none'} />}
      tooltip="Flag overrides"
      tooltipPlacement="right"
      textAlign="left"
      fill
    >
      {!isCollapsed && (
        <div className="flex justify-between items-center gap-2">
          Flags
          {overriddenFlagCount > 0 && (
            <Tag minimal intent="warning">
              {overriddenFlagCount}
            </Tag>
          )}
        </div>
      )}
    </ClarionButton>
  );

  return (
    <ClarionPopover title={`Flag Overrides (${overriddenFlagCount})`} target={button}>
      {Object.entries(flags).map(([flag, enabled]) => (
        <Switch
          key={flag}
          children={flag + (typeof flagOverrides[flag as FlagName] === 'boolean' ? ' *' : '')}
          defaultSelected={enabled}
          onChange={() => setFlagOverride(flag as FlagName, !enabled)}
        />
      ))}
      <p className="opacity-80 mt-2!">* overridden</p>
      <Divider className="mb-3" />
      <ClarionButton
        intent="primary"
        fill
        onClick={() => window.location.reload()}
        children="Save"
      />
      <ClarionButton
        className="mt-3"
        fill
        variant="minimal"
        onClick={resetFlagOverrides}
        children="Reset overrides"
      />
      {/* Wrapping in fragment prevents popover from affecting link styles */}
    </ClarionPopover>
  );
}
